/**
 * useHtmlActionBridge - lets a game mode drive an action panel from its own HTML.
 *
 * The panel stays the interaction contract (allowed actions, timeout, barrier);
 * the HTML rendered in the phone info area is an alternative renderer. Elements
 * marked with `data-action-id` submit the matching panel action when clicked,
 * and are reported back so the panel can hide duplicate buttons.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { HapticFeedbackService } from '@/services/hapticFeedback'

const EMPTY_IDS: ReadonlySet<string> = new Set<string>()

function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) {
    if (!b.has(id)) return false
  }
  return true
}

export interface UseHtmlActionBridgeOptions {
  /** Container holding the game-mode HTML. */
  containerRef: React.RefObject<HTMLElement | null>
  /** Action IDs of the current question — anything else in the HTML is ignored. */
  allowedActionIds: ReadonlySet<string>
  /** Whether the current panel opted into the bridge (display_mode="html_bridge"). */
  enabled: boolean
  /** Submits an action; rejects when the submit fails. */
  onSubmit: (actionId: string) => Promise<void>
  /**
   * Current HTML string. Needed as an effect dependency: the container mounts
   * conditionally and a ref change does not trigger a re-render.
   */
  html: string
}

/**
 * Wires clicks on `data-action-id` elements to the panel submit handler.
 *
 * @returns Action IDs currently present in the container HTML.
 */
export function useHtmlActionBridge({
  containerRef,
  allowedActionIds,
  enabled,
  onSubmit,
  html,
}: UseHtmlActionBridgeOptions): ReadonlySet<string> {
  // Tagged with the HTML it was scanned from, so a result belonging to previous
  // HTML (or to a disabled bridge) is discarded by derivation instead of by an
  // extra state reset inside the effect.
  const [scanResult, setScanResult] = useState<{
    html: string
    ids: ReadonlySet<string>
  } | null>(null)

  // The bridge bypasses the panel components, where click dedup normally lives.
  const inFlightRef = useRef(false)

  // Kept in refs so the listener/observer effect does not re-subscribe on every
  // render of the parent view. Synced in an effect, not during render: a render
  // that never commits must not leak its values into the live listener.
  const allowedRef = useRef(allowedActionIds)
  const submitRef = useRef(onSubmit)

  useEffect(() => {
    allowedRef.current = allowedActionIds
    submitRef.current = onSubmit
  }, [allowedActionIds, onSubmit])

  const scan = useCallback(
    (container: HTMLElement, scannedHtml: string) => {
      const found = new Set<string>()
      container.querySelectorAll('[data-action-id]').forEach((element) => {
        const actionId = element.getAttribute('data-action-id')
        if (actionId) found.add(actionId)
      })
      setScanResult((prev) =>
        prev !== null && prev.html === scannedHtml && sameIds(prev.ids, found)
          ? prev
          : { html: scannedHtml, ids: found }
      )
    },
    []
  )

  // useLayoutEffect, not useEffect: the initial scan reads the freshly injected DOM
  // and feeds it back into state, so it must run before paint — otherwise the panel
  // briefly renders buttons for actions that the HTML already bridges.
  useLayoutEffect(() => {
    if (!enabled) return

    const container = containerRef.current
    if (!container) return

    const handleClick = (event: Event) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const element = target.closest('[data-action-id]')
      if (!element) return

      const actionId = element.getAttribute('data-action-id')
      if (!actionId) return

      if (!allowedRef.current.has(actionId)) {
        console.error(
          `[useHtmlActionBridge] data-action-id "${actionId}" is not an action of the current question`
        )
        return
      }

      if (inFlightRef.current) return
      inFlightRef.current = true
      HapticFeedbackService.notification()

      submitRef
        .current(actionId)
        .then(() => {
          HapticFeedbackService.success()
        })
        .catch((err: unknown) => {
          console.error('[useHtmlActionBridge] Failed to submit action:', err)
          HapticFeedbackService.error()
        })
        .finally(() => {
          inFlightRef.current = false
        })
    }

    // Capture phase: game-mode scripts (unsafe_html) attach their own handlers
    // inside this container, and a stopPropagation() there would silently kill
    // the bridge if we listened while bubbling.
    container.addEventListener('click', handleClick, true)

    // Initial scan: the HTML is injected together with the container, so the
    // observer alone would never report the elements that already exist.
    scan(container, html)

    // Game-mode scripts (unsafe_html) and useAnimatedEmoji rewrite this DOM
    // after mount, so bridged elements may appear or disappear at any time.
    // Every notification triggers a full rescan — an element removed from the
    // HTML must leave the set, otherwise its panel button would stay hidden
    // forever and the player would lose the only way to answer.
    const observer = new MutationObserver(() => scan(container, html))
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributeFilter: ['data-action-id'],
    })

    return () => {
      container.removeEventListener('click', handleClick, true)
      observer.disconnect()
    }
  }, [enabled, containerRef, html, scan])

  // A result from a previous HTML — or from a disabled bridge — must not leak into
  // the panel, otherwise it would keep hiding buttons the player still needs.
  if (!enabled || scanResult === null || scanResult.html !== html) {
    return EMPTY_IDS
  }
  return scanResult.ids
}
